import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { getMacroColors } from "../../helpers/macroColors";
import { calculateMacroPercentages } from "../../helpers/macroPercentages";
import MultiPartCircularProgress from "./MultiPartCircularProgress";

type Props = {
  cals: number;
  carbs: number;
  protein: number;
  fat: number;
  ringSize?: number;
};

/**
 * The app's `DayIntakePieChartContainer`: the segmented calorie ring over a
 * three-column legend (dot, macro name, grams). Also used by the details
 * panel's nutrition facts, like the app's `ItemNutritionFacts`.
 */
const MacroRingWithLegend = ({
  cals,
  carbs,
  protein,
  fat,
  ringSize = 120,
}: Props) => {
  const theme: Theme = useTheme();
  const percentages = calculateMacroPercentages({ carbs, protein, fat }, cals);
  const colors = getMacroColors(theme);

  const legend = [
    { label: "Carbs", value: carbs, color: colors.carbs },
    {
      label: "Protein",
      value: protein,
      color: colors.protein,
    },
    { label: "Fat", value: fat, color: colors.fat },
  ];

  return (
    <Box sx={styles.container}>
      <MultiPartCircularProgress
        size={ringSize}
        segments={[
          {
            percentage: percentages.carbs,
            color: colors.carbs,
          },
          {
            percentage: percentages.protein,
            color: colors.protein,
          },
          {
            percentage: percentages.fat,
            color: colors.fat,
          },
        ]}
        textInfo={cals.toFixed(0)}
      />
      <Box sx={styles.legend}>
        {legend.map((entry) => (
          <Box key={entry.label} sx={styles.legendItem}>
            <Box sx={styles.titleRow}>
              <Box sx={styles.dot(entry.color)} />
              <Box sx={styles.label(theme)}>{entry.label}</Box>
            </Box>
            <Box sx={styles.value(theme)}>
              {entry.value.toFixed(0)}
              <Box component="span" sx={styles.unit(theme)}>
                {" "}
                g
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MacroRingWithLegend;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    gap: "10px",
    padding: "20px",
  },
  legend: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginTop: "10px",
  },
  legendItem: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    padding: "0 8px",
    whiteSpace: "nowrap" as "nowrap",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "4px",
  },
  dot: (color: string) => ({
    width: "8px",
    height: "8px",
    borderRadius: "4px",
    backgroundColor: color,
  }),
  label: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  value: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  unit: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
